// What a user's change to a map does: the request, and what it means for the state the windows and the sidebar read (R41, R46). Each action runs
// against a `ctx` that the provider builds from its own state and methods (ContextProvider.ctx), so that the actions are tested without React.
//
//   ctx = {
//     projectId,
//     getState(),                      the current {loadedMaps, folderStructure, windows}
//     setState(changes),               the provider's setState: an object of the keys that change, or a function of the state that answers one (or
//                                      null for no change). Every write of loadedMaps is the function form, which merges one map into the loadedMaps
//                                      of that moment, so that a write that began earlier cannot drop a map that another action added meanwhile.
//     alert(message, colour),
//     confirm(message, colour, cancelable),   resolves the toast's answer: true for Ok, false for Cancel
//     closeWindow(mapId),              removes the window and writes the stored layout
//     hexDirty(changedBytes),          the hex viewer's bytes changed (true: some bytes did, not counted)
//     captureException(error, mapName),       reportError, with the project bound and the map named by the caller
//     mapWrites,                       the provider's queue of each map's writes (context/mapWrites.js)
//     retryOptions,                    optional {sleep, jitter}, withRetry's third argument, which also gives the sleep of the hold after a lost
//   }                                  request. The tests give an instant sleep; the provider leaves it undefined, so the waits use real timers.
//
// A map's id is its name (R55), so `mapId` is also what the URL carries. The file has four parts. The state helpers write one change to the
// state. The send steps (sendMapSave, sendAxisSave) make the request and have no other effect, so that the actions that chain several saves (the
// reset) can send one after another and decide for themselves what the user sees. handleSaveFailure holds what each kind of failure means,
// for every action that saves. The public actions (saveMap, saveAxis, undo, resetMap, refreshMap) put a step on the map's queue. The undo has its own failure handler
// (handleUndoFailure): it has no Not saved state, and is never sent again.
//
// A map is saved in three parts, which the user edits apart: its data, and its X and Y axes. Each part has a mark of its own (`unsavedParts` on the
// loaded map: {data, X, Y}), so that a save that lands for one part never takes the Not saved mark off another part's edit; `notSaved`, which the badge
// reads, is the derived "any part is unsaved, or the state of the map is unknown".
//
// One write of a map is on the wire at a time (the Fable ruling on the order of a map's writes). The API's lock has no order, so two requests of one map
// that wait for it together can commit in either order, and a write replaces the whole map; the queue is what makes the order of the writes the order of
// the edits. A public action only queues. The step it queues, the failure handler and the retries all run inline inside that step, and a retry is the
// inner step, never the public action: a public call made from inside a step waits for the step that made it, which would never end.
//
// The values of a save are the values on screen: the matrices of the modified table of the loaded map, in which an edit is made in place (and
// Window.save puts the copy that a gradient drag edits into it). A save sends them when it runs, so a save that waited for another sends every edit
// made since it was asked for, and the saves asked for meanwhile collapse into it. A save that fails leaves them there, marked Not saved (`notSaved` on
// the map), and what Ok sends again is what that table holds then.
import equals from "fast-deep-equal";

import * as MapController from "controller/MapController";
import {dropQueuedWrites, enqueueMapWrite, hasQueuedSave} from "context/mapWrites";
import {ApiError, isMapNotFound, NoResponseError} from "repository/errors";
import {COPY, copyFor} from "text/errors";
import {setMapFavourited, setMapsModified} from "util/FolderStructure";
import {axisLetter, buildMap, tableWithUploadedAxes} from "util/mapModel";
import {HOLD_AFTER_LOST_MS, sleepFor} from "util/retry";
import {isReadLimited} from "util/rateLimit";

// ---- State helpers -----------------------------------------------------------------------------------------------------------------------
// Each write is a function of the state as it is when the write is made, never of a state read before an await. A save waits for its retries (seven
// seconds of sleep, and the API waits up to five for its lock before each answer) and, after the retries, for the user: a map may have been opened,
// or the listing replaced, by then, and a write that carried the old loadedMaps over would drop it.

// The three parts of a map that are saved apart, the key of the axis each of the two axis parts is, and the marks of a map with nothing unsaved.
const PARTS = ["data", "X", "Y"];
const AXIS_KEYS = {X: "xAxis", Y: "yAxis"};
const NO_PARTS = Object.freeze({data: false, X: false, Y: false});

// The marks of a loaded map. A map that has none (it was not built by buildMap) has nothing unsaved.
const partsOf = map => map.unsavedParts ?? NO_PARTS;
const anyUnsaved = unsavedParts => Object.values(unsavedParts).some(Boolean);

// The map with its marks set: `notSaved`, which the badge reads, is derived from them, so no write sets it alone. A map is not saved when a part of it is
// not, or when what the API holds is not known (`stateUnknown`: a request of it got no answer, and the read after it failed). The key is left out of a
// map that is not unknown, as buildMap leaves it.
const withMarks = (map, unsavedParts, stateUnknown) => {
    const {stateUnknown: previous, ...rest} = map;
    return {...rest, unsavedParts, notSaved: anyUnsaved(unsavedParts) || Boolean(stateUnknown), ...(stateUnknown ? {stateUnknown: true} : {})};
};

// The last good payload, built again: the in-place edit of the table is gone, and so are the marks of the parts. A rebuild is not an outcome, though: it
// says nothing new about the server, so a map whose state is unknown stays unknown, and with it Not saved (the badge reads `notSaved`).
const rebuildFromRaw = (ctx, mapId) => {
    ctx.setState(state => {
        const current = state.loadedMaps[mapId];
        if (!current) return null;

        const rebuilt = buildMap(current.raw);
        return {loadedMaps: {...state.loadedMaps, [mapId]: current.stateUnknown ? withMarks(rebuilt, NO_PARTS, true) : rebuilt}};
    });
};

// The values of a part on screen are not the ones the API holds. The map is the same one, with the same tables, so what the user typed stays where it is.
// `unknown` is for a request that got no answer: it may have been written, so the editor does not know what the API holds either (`stateUnknown`).
const markNotSaved = (ctx, mapId, part, {unknown = false} = {}) => {
    ctx.setState(state => {
        const current = state.loadedMaps[mapId];
        return current ? {loadedMaps: {...state.loadedMaps, [mapId]: withMarks(current, {...partsOf(current), [part]: true}, unknown || current.stateUnknown)}} : null;
    });
};

// The values of a part on screen are the ones the API holds after all (a save of it found nothing to send): its mark comes off, the others' do not, and
// the table is as it was.
const clearNotSaved = (ctx, mapId, part) => {
    ctx.setState(state => {
        const current = state.loadedMaps[mapId];
        return current ? {loadedMaps: {...state.loadedMaps, [mapId]: withMarks(current, {...partsOf(current), [part]: false}, current.stateUnknown)}} : null;
    });
};

// Whether the map is loaded and its part is marked Not saved now.
const isPartUnsaved = (ctx, mapId, part) => {
    const map = ctx.getState().loadedMaps[mapId];
    return Boolean(map && partsOf(map)[part]);
};

// The map is gone from the release (404 map_not_found): it leaves loadedMaps, so that nothing of it is sent again. A map whose state was unknown would
// otherwise stay unknown, and every landed save and every reconnection would send it again and get the 404 again.
const forgetMap = (ctx, mapId) => {
    ctx.setState(state => {
        if (!state.loadedMaps[mapId]) return null;

        const {[mapId]: gone, ...kept} = state.loadedMaps;
        return {loadedMaps: kept};
    });
};

// What a 404 map_not_found means, whichever write or read got it (the one rule of preflight Tasks 8 and 9 and the Fable ruling): the map is not in this
// release, so its window closes without a word, what was queued for it is dropped, and it leaves loadedMaps.
const dropGoneMap = (ctx, mapId) => {
    ctx.closeWindow(mapId);
    dropQueuedWrites(ctx, mapId);
    forgetMap(ctx, mapId);
};

// Whether the map is loaded now. A step of a map that is not (it was found gone, between the request for the step and its turn) does nothing.
const isLoaded = (ctx, mapId) => Boolean(ctx.getState().loadedMaps[mapId]);

// The values the window shows of the map's data: those of the modified table of the loaded map, which holds an edit made in place. `fallback` is for a
// map that is no longer loaded.
const valuesOnScreen = (ctx, mapId, fallback) => {
    const map = ctx.getState().loadedMaps[mapId];
    return map ? map.modified.dataAxis.matrix.values : fallback;
};

// The values the window shows of one of the map's axes: the flat list in the modified table of the loaded map, in which an edit of the axis is made in
// place. `fallback` is for a map that is no longer loaded, or has no such axis.
const axisValuesOnScreen = (ctx, mapId, axis, fallback) => {
    const table = ctx.getState().loadedMaps[mapId]?.modified[AXIS_KEYS[axis]];
    return table ? table.matrix.values[0] : fallback;
};

/**
 * Puts a map the API has answered with on screen, and sets the swModified flags of the maps the answer says are affected, which the sidebar follows.
 * `raw`, the flags and the marks are the answer's: a map built from an answer is marked neither Not saved nor unknown. That is for the part the answer
 * is the outcome of; a save of one part says nothing about the others, so their marks stay (`part` names the part: 'data', 'X' or 'Y'). A read is an
 * outcome for every part (`part` null).
 *
 * The one thing it can leave alone is the modified table on screen. While a save of the map is queued, the table holds what the user has typed since,
 * and that save is about to send it; and while another part is unsaved, the table holds the edit of that part, which the answer knows nothing of, and
 * which would vanish from the screen while its badge stayed on. In both cases the whole table stays (it is not merged part by part), and the last
 * outcome of the queue, with nothing unsaved, replaces it. Whether a save is queued is read before the state is written, not inside the updater, which
 * runs when React says. A map that is not loaded (it was found gone) is not put back: only the flags are set.
 * @param {Object} ctx
 * @param {string} mapId
 * @param {LoadedMap} map - built from the answer
 * @param {Object<string, boolean>} flags - the swModified flag of each map the answer names
 * @param {?string} [part] - 'data', 'X' or 'Y' for the outcome of a save of that part; null for a read
 */
export const applyOutcome = (ctx, mapId, map, flags, part = null) => {
    const queued = hasQueuedSave(ctx, mapId);

    ctx.setState(state => {
        const folderStructure = setMapsModified(flags, state.folderStructure);
        const current = state.loadedMaps[mapId];
        if (!current) return {folderStructure};

        const stillUnsaved = part === null ? NO_PARTS : {...partsOf(current), [part]: false};
        const keepTable = queued || anyUnsaved(stillUnsaved);
        const next = keepTable ? withMarks({...map, modified: current.modified}, stillUnsaved, false) : map;

        return {loadedMaps: {...state.loadedMaps, [mapId]: next}, folderStructure};
    });
};

// The map as the API holds it now, for when a request got no answer and so the API may or may not have the edit (R41). The read has no list of
// affected maps, so the map's own swModified is its flag in the sidebar. If this read fails as well, nothing changes: the action that asked for it
// has marked the map already, and the edit stays on screen, in the same table. The user has been told already, so there is no new toast; a map that is
// gone closes its window quietly and its queue is dropped, and only a failure that nothing explains is reported. It never throws, so the action that
// asked for it can finish. It answers whether the read landed: a request that got no answer may have been written, with a changedBytes that is not
// known, so an action whose read lands tells the hex viewer that bytes may have changed (hexDirty(true)); a read that fails tells it nothing.
// The one read of a map from inside a queue step, and how it ended: 'landed', 'limited' (the API's read limit, a 429: a burst, or the daily cap) or 'failed'.
// It never waits for a pause (wait: false): a step that waited out a 60 s pause would hold the map's queue, and every save behind it. During a pause, or
// under the daily cap, the limiter rejects at once with no request, and that is a failed read like any other: the map stays on its last payload (DQ3), or, after a
// lost answer, stays unknown. The user has been told by the limiter's own toast, so a 429 is neither reported nor toasted here.
const readMap = async (ctx, mapId) => {
    if (!isLoaded(ctx, mapId)) return 'failed';

    try {
        const map = await MapController.getMap(ctx.projectId, mapId, {wait: false});
        applyOutcome(ctx, mapId, map, {[mapId]: map.swModified});
        return 'landed';
    } catch (error) {
        if (isMapNotFound(error)) {
            dropGoneMap(ctx, mapId);
        } else if (isReadLimited(error)) {
            return 'limited';
        } else if (!(error instanceof NoResponseError)) {
            ctx.captureException(error, mapId);
        }
    }
    return 'failed';
};

const reloadMap = async (ctx, mapId) => (await readMap(ctx, mapId)) === 'landed';

/**
 * Reads the map again and puts it on screen, as a step of the map's queue: after the write that is running, and not at all while a save is queued
 * behind it, because every queued save ends by refreshing the map from its answer. For a read that would replace a loaded map and is not part of a
 * write's own step (a window opened, another map's refresh). It never rejects.
 * @param {Object} ctx
 * @param {string} mapId
 * @returns {Promise<?string>} how the read ended, readMap's word: 'landed', 'limited' (the API's read limit refused it: a 429, a burst or the daily cap, so that a
 *   chain of refreshes can stop) or 'failed'; null when the refresh was skipped because a step of the map is queued
 */
export const refreshMap = async (ctx, mapId) => {
    let outcome = null;

    await enqueueMapWrite(ctx, mapId, {kind: 'reload', part: null, run: async () => {
        outcome = await readMap(ctx, mapId);
    }});
    return outcome;
};

// ---- The send steps ----------------------------------------------------------------------------------------------------------------------

/**
 * @typedef {Object} SaveResult - a save that was sent: what the API answered, with the map built from it
 * @property {LoadedMap} map - built from the answer, which is kept whole as the map's `raw`
 * @property {string[]} affectedMaps - the maps the save touched
 * @property {Object<string, boolean>} affectedMapsState - the swModified flag of each of them
 * @property {?string} undoBatchId - null when nothing changed
 * @property {number} changedBytes
 */

// The values of the map's data axis that the API holds: the working file's, or the upload's while the project has no working file (the same section
// buildMap shows as `modified`). The payload is never changed in place, unlike the table built from it, so it is what the last save or read said.
const currentValuesOf = raw => (raw.modified ?? raw.uploaded).dataAxis.values;

// The same for one of the axes ('X' or 'Y'): undefined for a map that has no such axis (a value map has none).
const currentAxisValuesOf = (raw, axis) => (raw.modified ?? raw.uploaded)[AXIS_KEYS[axis]]?.values;

// A save that finds nothing to send, and ends the map's queue, leaves the screen on the server's answer (the last step's outcome is the table). The table
// was kept while a save was queued (R4), and a no-op save never gets an outcome of its own to replace it, so the table is rebuilt from `raw` here, when
// it is not what `raw` holds: the data, X and Y values of the section the API holds now. Only when nothing else says the screen is the user's: a save is
// queued, a part is unsaved (the table holds that edit), or the state of the map is unknown (`raw` may be older than the server). Otherwise the table
// object stays, as the user has been typing into it.
const settleTable = (ctx, mapId) => {
    const map = ctx.getState().loadedMaps[mapId];
    if (!map || hasQueuedSave(ctx, mapId) || anyUnsaved(partsOf(map)) || map.stateUnknown) return;

    const heldByServer = equals(valuesOnScreen(ctx, mapId), currentValuesOf(map.raw))
        && ["X", "Y"].every(axis => equals(axisValuesOnScreen(ctx, mapId, axis), currentAxisValuesOf(map.raw, axis)));
    if (!heldByServer) rebuildFromRaw(ctx, mapId);
};

/**
 * The request of a map save, and nothing else: no state is written, no toast is shown, the hex viewer is not told (that is for the action that
 * called it). It never rejects.
 *
 * A save whose values equal the ones the API holds is not sent. A drag release that moved nothing saves today (EDI A5), and re-encoding an
 * unchanged float64 cell can change its last bits, which would turn the map's swModified on though the user changed nothing (API 5.7). The exception
 * is a map whose state is unknown (a request of it got no answer): its payload may be older than the server's, so equal to it proves nothing, and the
 * values are sent. A map that is no longer loaded has nothing to send.
 * @param {Object} ctx
 * @param {string} mapId
 * @param {number[][]} values - the values of the data axis, as the editor holds them
 * @returns {Promise<{ok: true, result: ?SaveResult}|{ok: false, error: Error}>} `result` is null when nothing was sent
 */
export const sendMapSave = async (ctx, mapId, values) => {
    try {
        const map = ctx.getState().loadedMaps[mapId];
        if (!map) return {ok: true, result: null};
        if (!map.stateUnknown && equals(values, currentValuesOf(map.raw))) return {ok: true, result: null};

        return {ok: true, result: await MapController.saveMap(ctx.projectId, mapId, values, ctx.retryOptions)};
    } catch (error) {
        return {ok: false, error};
    }
};

/**
 * The request of an axis save (API 1.3), and nothing else, as sendMapSave is for the data: no state is written, no toast is shown, the hex viewer is not
 * told. It never rejects. The same rule skips a save whose values equal the ones the API holds, against the raw payload's axis, and the same exception
 * sends them while the state of the map is unknown. A map that is no longer loaded, or has no such axis, has nothing to send.
 * @param {Object} ctx
 * @param {string} mapId
 * @param {'X'|'Y'} axis
 * @param {number[]} values - the flat list of the axis's values, exactly the size of the template row (R1)
 * @returns {Promise<{ok: true, result: ?SaveResult}|{ok: false, error: Error}>} `result` is null when nothing was sent
 */
export const sendAxisSave = async (ctx, mapId, axis, values) => {
    try {
        const map = ctx.getState().loadedMaps[mapId];
        if (!map) return {ok: true, result: null};

        const stored = currentAxisValuesOf(map.raw, axis);
        if (!stored || (!map.stateUnknown && equals(values, stored))) return {ok: true, result: null};

        return {ok: true, result: await MapController.saveAxis(ctx.projectId, mapId, axis, values, ctx.retryOptions)};
    } catch (error) {
        return {ok: false, error};
    }
};

// ---- The failure handler -----------------------------------------------------------------------------------------------------------------

// The codes of a 400 that mean the values the user typed cannot be kept. The status is part of the test: the API sends these codes with a 400 only.
const REFUSED = ["invalid_values", "invalid_target", "read_only_axis"];

/**
 * What a failed save means for the user, whichever action sent it. It runs inside the failed step, so it holds the map's queue until it has done. It
 * never rejects.
 *  - A 400 that refuses the values: the map is built again from the last good payload, so the edit made in place is discarded, and the user is told why.
 *  - A 409 project_locked that the retries did not outlast: the part is marked Not saved, with the values left on screen, and the user is asked whether
 *    to try again. Nothing else of the map is sent while the question is open. The answer counts only while the part is still marked: a map that was
 *    removed has nothing to act on, and a save that has landed since has settled the part. Ok runs `retrySend`, which sends the values on screen then, so
 *    that a newer edit is never overwritten by older values (when nothing was typed since, they are the first body). Cancel builds the map again from the
 *    last good payload, as a refused save does. Questions about two maps that one answer settles each run their own map's retry: the same request, and
 *    the save is idempotent.
 *  - No response, after the retry: the request may or may not have been written, and the editor does not know which. The part is marked Not saved at
 *    once, with the state of the map unknown, and the user is told. A request that failed at once may still be on its way to the API, so the map is read
 *    only after a hold (HOLD_AFTER_LOST_MS), unless the request timed out, which is a minute old already. The read puts the API's map on screen, the
 *    sidebar following it (and every mark comes off); with a save queued behind, the table stays and that save sends it. If the read fails too, the
 *    marks stay: the edit stays on screen and is sent again, when the API answers a later save or the connection is back (resendUnknownMaps).
 *  - A 404 map_not_found: the map is not in this release, so its window closes without a word (R45), what was queued for it is dropped, and it leaves
 *    loadedMaps (a map that stayed would be sent again by every later landed save and reconnection).
 *  - Anything else: the error is reported, with the map (the typed error, which has the status, the code and the route and none of the request's
 *    headers), the user gets the copy table's row for its code (a write's store_error, project_not_converted or project_conversion_failed), or the
 *    generic text, and the part is marked Not saved: the edit stays on screen and the badge says that the API does not have it. There is no question,
 *    because COPY.notSaved names a busy project.
 * @param {Object} ctx
 * @param {string} mapId
 * @param {Error} error - what a send step answered, or any other action's failure
 * @param {function(): Promise<void>} retrySend - runs the action's inner step again, with the values on screen at that moment (never the public action,
 *   which would queue behind the step that is calling it)
 * @param {'data'|'X'|'Y'} [part] - the part of the map that the failed step was saving, which is the one that is marked
 * @returns {Promise<void>} settled once the failure has been dealt with, retry included
 */
export const handleSaveFailure = async (ctx, mapId, error, retrySend, part = "data") => {
    if (error instanceof ApiError) {
        if (error.status === 400 && REFUSED.includes(error.code)) {
            rebuildFromRaw(ctx, mapId);
            ctx.alert(copyFor(error), "danger");
            return;
        }

        if (error.status === 409 && error.is("project_locked")) {
            markNotSaved(ctx, mapId, part);
            const answer = await ctx.confirm(COPY.notSaved, "danger", true);
            if (!isPartUnsaved(ctx, mapId, part)) return;

            if (answer) {
                await retrySend();
            } else {
                rebuildFromRaw(ctx, mapId);
            }
            return;
        }

        if (isMapNotFound(error)) {
            dropGoneMap(ctx, mapId);
            return;
        }
    } else if (error instanceof NoResponseError) {
        markNotSaved(ctx, mapId, part, {unknown: true});
        ctx.alert(COPY.noResponse, "danger");
        if (!error.timedOut) await sleepFor(ctx.retryOptions, HOLD_AFTER_LOST_MS);
        if (await reloadMap(ctx, mapId)) {
            ctx.hexDirty(true);
            // An axis save changes the maps that share the axis, and the answer that would have named them was lost. A data save changes no other map.
            if (part !== "data") refreshOtherWindows(ctx, mapId);
        }
        return;
    }

    markNotSaved(ctx, mapId, part);
    ctx.captureException(error, mapId);
    ctx.alert(copyFor(error), "danger");
};

// ---- The public actions ------------------------------------------------------------------------------------------------------------------

// Whether the map is loaded and its state is unknown now (a request of it got no answer, and the read after it failed).
const isUnknown = (ctx, mapId) => Boolean(ctx.getState().loadedMaps[mapId]?.stateUnknown);

// A landed save of one part of a map that was unknown clears the map's unknown state, and so takes the map out of resendUnknownMaps, while another
// part may still be marked (the request that was lost was for it). Nothing else would send that part again, so the step that landed starts it, through
// the part's public action: it queues behind this step and runs against the fresh payload, and either finds nothing to send (the lost write had
// committed, and its mark comes off) or sends the values on screen. Detached: the step is this map's own, and must not wait for its own queue. A part
// that a known failure left marked is not sent again here; the map was not unknown.
const resendOtherParts = (ctx, mapId, landedPart) => {
    const map = ctx.getState().loadedMaps[mapId];
    if (!map) return;

    PARTS.filter(part => part !== landedPart && partsOf(map)[part]).forEach(part => {
        if (part === "data") saveMap(ctx, mapId, valuesOnScreen(ctx, mapId));
        else saveAxis(ctx, mapId, part, axisValuesOnScreen(ctx, mapId, part));
    });
};

// The step of a map save. The values it is given are for a map that is no longer loaded, which has nothing to send; the values it sends are those on
// screen when it runs.
const runMapSave = async (ctx, mapId, values) => {
    if (!isLoaded(ctx, mapId)) return;

    const sent = await sendMapSave(ctx, mapId, valuesOnScreen(ctx, mapId, values));

    if (!sent.ok) {
        await handleSaveFailure(ctx, mapId, sent.error, () => runMapSave(ctx, mapId, values), "data");
        return;
    }
    if (sent.result === null) {
        if (isPartUnsaved(ctx, mapId, "data")) clearNotSaved(ctx, mapId, "data");
        settleTable(ctx, mapId);
        return;
    }

    const wasUnknown = isUnknown(ctx, mapId);
    applyOutcome(ctx, mapId, sent.result.map, sent.result.affectedMapsState, "data");
    if (wasUnknown) resendOtherParts(ctx, mapId, "data");
    ctx.hexDirty(sent.result.changedBytes);
    // The API answered, so it can be reached: the other maps whose state is unknown are sent now. Their queues are their own, so this step does not wait.
    resendUnknownMaps(ctx, mapId);
};

// The step of an axis save: the same as the data's, for the part 'X' or 'Y'. The other windows that show a map the save affected are read again, and the
// step does not wait for them (refreshOpenWindows starts the reads detached); the toast that tells the user other maps were affected comes last.
const runAxisSave = async (ctx, mapId, axis, values) => {
    if (!isLoaded(ctx, mapId)) return;

    const sent = await sendAxisSave(ctx, mapId, axis, axisValuesOnScreen(ctx, mapId, axis, values));

    if (!sent.ok) {
        await handleSaveFailure(ctx, mapId, sent.error, () => runAxisSave(ctx, mapId, axis, values), axis);
        return;
    }
    if (sent.result === null) {
        if (isPartUnsaved(ctx, mapId, axis)) clearNotSaved(ctx, mapId, axis);
        settleTable(ctx, mapId);
        return;
    }

    const {map, affectedMaps, affectedMapsState} = sent.result;
    const wasUnknown = isUnknown(ctx, mapId);
    applyOutcome(ctx, mapId, map, affectedMapsState, axis);
    if (wasUnknown) resendOtherParts(ctx, mapId, axis);
    refreshOpenWindows(ctx, affectedMaps.filter(name => name !== mapId));
    ctx.hexDirty(true);
    resendUnknownMaps(ctx, mapId);
    if (affectedMaps.length > 1) ctx.alert(COPY.axisAffected(affectedMaps.length - 1), "danger");
};

/**
 * Reads again the maps an axis save affected, for the ones that have a window open: another map that shares the axis has new values on its sheet.
 * Each read is a step of its map's queue (refreshMap), and they go one after another (they count as map reads, R48). The caller must not wait for
 * this: the reads queue behind whatever the other maps are doing, and a map whose own step waited for ours would hang both for good (two maps that
 * share an axis can both have an axis save running at once), so the chain is started detached after the caller's outcome is applied, and the caller
 * goes on. The promise it returns is for a test; it never rejects, and a read that fails leaves that map on its last payload. The chain stops at the first
 * read the API's read limit refused (a 429, a burst or the daily cap; refreshMap answers 'limited'): the maps after it are not asked for and keep their last
 * payloads too (DQ3). A read that failed for another reason does not stop it.
 * @param {Object} ctx
 * @param {string[]} names - the other maps named in `affectedMaps`
 * @returns {Promise<void>} settled when the reads have
 */
export const refreshOpenWindows = async (ctx, names) => {
    // Chosen when the call is made, not when the chain reaches a map.
    const open = names.filter(name => ctx.getState().windows.some(window => window.mapId === name));

    for (const name of open) {
        try {
            if ((await refreshMap(ctx, name)) === 'limited') break;
        } catch (error) {
            ctx.captureException(error, name);
        }
    }
};

// The other open windows read again, for a write whose answer was lost and so did not say which maps it affected (an axis save, an undo). Detached, as
// every cross-map read of a step: the step does not wait for another map's queue. Started after the map's own re-read has landed.
const refreshOtherWindows = (ctx, mapId) => {
    refreshOpenWindows(ctx, ctx.getState().windows.map(window => window.mapId).filter(name => name !== mapId));
};

/**
 * Sends again what is on screen of every loaded map whose state is unknown (a request of it got no answer, and neither did the read that followed).
 * The user asked for those edits by making them, the badge says that the API may not have them, and sending a part again is idempotent. The parts that
 * are marked are sent, each through its public action, so it waits for whatever its map is doing: the data through saveMap, an axis through saveAxis.
 * A map with no part marked is sent as its data, which is what a map in that state has always been sent. Called when a save has landed (the API can be
 * reached) and when the connection is back (ContextProvider._checkNetworkConnection).
 * @param {Object} ctx
 * @param {?string} [exceptMapId] - a map to leave out: the one whose save has just landed
 * @returns {Promise<void[]>} settled when the re-sends have
 */
export const resendUnknownMaps = (ctx, exceptMapId = null) => {
    const {loadedMaps} = ctx.getState();
    const resend = (mapId, part) => (part === "data" ? saveMap(ctx, mapId, valuesOnScreen(ctx, mapId)) : saveAxis(ctx, mapId, part, axisValuesOnScreen(ctx, mapId, part)));

    return Promise.all(
        Object.keys(loadedMaps)
            .filter(mapId => mapId !== exceptMapId && loadedMaps[mapId].stateUnknown)
            .flatMap(mapId => {
                const marked = PARTS.filter(part => partsOf(loadedMaps[mapId])[part]);
                return (marked.length > 0 ? marked : ["data"]).map(part => resend(mapId, part));
            })
    );
};

/**
 * Saves the values of a map's data, as a step of the map's queue: it waits for the write that is running, and the saves asked for while it waits join it
 * (the one step sends the values on screen when its turn comes). The map the API answers with replaces the loaded one (the values in it are the stored
 * ones, which can differ from what was sent), the sidebar's flags follow the API's `affectedMapsState`, and the hex viewer is told how many bytes
 * changed. Values that are the API's own are not sent, and then the map matches the server: the data's Not saved mark comes off. A failure is
 * handleSaveFailure's, and Ok on a Not saved question runs the step again with the values on screen then.
 * @param {Object} ctx
 * @param {string} mapId
 * @param {number[][]} values - the matrix values of the map's data axis, rows by columns: the values of the modified table of the loaded map
 * @returns {Promise<void>} settles when the step does, whatever came of it
 */
export const saveMap = (ctx, mapId, values) => enqueueMapWrite(ctx, mapId, {kind: 'save', part: 'data', run: () => runMapSave(ctx, mapId, values)});

/**
 * Saves the values of one of a map's axes (API 1.3), as a step of the map's queue with the part 'X' or 'Y': the saves of one axis asked for while one
 * waits join it, and the step sends the axis values on screen when its turn comes. An axis the template marks read-only is refused first, with a toast
 * and no request (the legacy API accepted such a save without a word; the new one answers 400 read_only_axis, API 4.5). The map the API answers with
 * replaces the loaded one for this part (a data edit that is Not saved stays on screen with its mark), every map the save affected gets its own sidebar
 * flag from `affectedMapsState` (R46), the other windows that show an affected map are read again, the hex viewer is told, and the user is told when
 * other maps were affected. A failure is handleSaveFailure's, for this part.
 * @param {Object} ctx
 * @param {string} mapId
 * @param {'X'|'Y'} axis - the letter of the axis (see axisLetter)
 * @param {number[]} values - the flat list of the axis's values: those of the modified table of the loaded map
 * @returns {Promise<void>} settles when the step does, whatever came of it; the re-reads of other windows are not waited for
 */
export const saveAxis = (ctx, mapId, axis, values) => {
    if (ctx.getState().loadedMaps[mapId]?.uploaded[AXIS_KEYS[axis]]?.readOnly) {
        ctx.alert(COPY.read_only_axis, "danger");
        return Promise.resolve();
    }

    return enqueueMapWrite(ctx, mapId, {kind: "save", part: axis, run: () => runAxisSave(ctx, mapId, axis, values)});
};

// ---- Undo --------------------------------------------------------------------------------------------------------------------------------

// What a failure of an undo means for the user. It runs inside the undo's step. It never rejects. `written` is whether the failed request was the POST,
// the one request that changes the map.
//  - A 409 undo_not_newest: another entry has become the newest since the list was read. The map is read again, and the user is told.
//  - A 404 undo_entry_not_found: there is nothing to undo any more (another window undid it). The map is read again, and the user is told.
//  - A 404 map_not_found: the map is not in this release, so its window closes without a word, what was queued for it is dropped, and it leaves loadedMaps.
//  - A 409 project_locked that the one retry did not outlast: the user is told that the project is busy. Nothing was undone.
//  - No response to the POST: an undo is never sent again, because a second one would revert one more entry, and the lost one may still commit at the
//    API's lock (R7). The user is told, the map is read only after the hold (unless the request timed out: it is a minute old already), and the read puts
//    what the API did on screen. Undo has no Not saved state, so a read that fails leaves the map on its last payload with no mark. No response to the
//    list is a failure like any other: nothing was written.
//  - Anything else is reported, with the map, and the user gets the copy table's row for its code or the generic text.
const handleUndoFailure = async (ctx, mapId, error, written) => {
    if (error instanceof ApiError) {
        if (error.status === 409 && error.is("undo_not_newest")) {
            ctx.alert(COPY.undo_not_newest, "danger");
            await reloadMap(ctx, mapId);
            return;
        }

        if (error.status === 404 && error.is("undo_entry_not_found")) {
            ctx.alert(COPY.nothingToUndo, "danger");
            await reloadMap(ctx, mapId);
            return;
        }

        if (isMapNotFound(error)) {
            dropGoneMap(ctx, mapId);
            return;
        }

        if (error.status === 409 && error.is("project_locked")) {
            ctx.alert(COPY.project_locked, "danger");
            return;
        }
    } else if (error instanceof NoResponseError && written) {
        ctx.alert(COPY.noResponse, "danger");
        if (!error.timedOut) await sleepFor(ctx.retryOptions, HOLD_AFTER_LOST_MS);
        if (await reloadMap(ctx, mapId)) {
            ctx.hexDirty(true);
            refreshOtherWindows(ctx, mapId);
        }
        return;
    } else if (error instanceof NoResponseError) {
        // The list got no answer: nothing was written, so there is nothing to read, and a lost request is not a bug to report.
        ctx.alert(copyFor(error), "danger");
        return;
    }

    ctx.captureException(error, mapId);
    ctx.alert(copyFor(error), "danger");
};

// The step of an undo. The list, the click-time check (R52: the entry that was newest when the list was read may not be undoable by now, and the API
// has the last word, but the user is told before the request), the question about a batch (a wizard's batch reverts on every map it touched, REQ E72),
// the POST and the outcome all run here, inside the map's turn, so that the newest entry is the newest at that moment.
const runUndo = async (ctx, mapId) => {
    if (!isLoaded(ctx, mapId)) return;

    let entries;
    try {
        entries = await MapController.getUndoList(ctx.projectId, mapId);
    } catch (error) {
        await handleUndoFailure(ctx, mapId, error, false);
        return;
    }

    if (entries.length === 0) return;

    const newest = entries[0];
    if (newest.undoable === false) {
        ctx.alert(COPY.undo_not_newest, "danger");
        return;
    }
    if (newest.batchSize > 1 && !(await ctx.confirm(COPY.undoBatchConfirm(newest.batchSize), "danger", true))) return;
    // The question can take a while; the map may have gone meanwhile.
    if (!isLoaded(ctx, mapId)) return;

    let undone;
    try {
        undone = await MapController.applyUndo(ctx.projectId, mapId, newest.id, ctx.retryOptions);
    } catch (error) {
        await handleUndoFailure(ctx, mapId, error, true);
        return;
    }

    const {map, affectedMaps, affectedMapsState} = undone;
    // The answer is the whole map, and an outcome for every part: the table, the marks and raw are the answer's.
    applyOutcome(ctx, mapId, map, affectedMapsState);
    refreshOpenWindows(ctx, affectedMaps.filter(name => name !== mapId));
    ctx.hexDirty(true);
    // The API answered, so it can be reached: the other maps whose state is unknown are sent now, detached (as a landed save does).
    resendUnknownMaps(ctx, mapId);
};

/**
 * Reverts the newest entry of a map's undo list (API 1.4, 1.5; R52), as a step of the map's queue of the kind 'undo': it waits for the write that is
 * running and for those queued before it, and is never collapsed, because each undo reverts one more entry. The list, the checks, the question and the
 * POST are in the step, so an undo asked for while a save is out reverts the newest entry when its turn comes. The list holds the map's data and axis
 * entries (API 4.7), so an axis edit is undone from the same button. The map the API answers with replaces the loaded one, whole; the other open
 * windows of the maps it affected are read again after the step has ended, and not waited for. An empty history is a silent no-op. The failures are
 * handleUndoFailure's.
 * @param {Object} ctx
 * @param {string} mapId
 * @returns {Promise<void>} settles when the step does, whatever came of it
 */
export const undo = (ctx, mapId) => enqueueMapWrite(ctx, mapId, {kind: "undo", part: null, run: () => runUndo(ctx, mapId)});

// ---- Reset -------------------------------------------------------------------------------------------------------------------------------

// The axes of a map that a reset puts back, in the order it sends them: the data, then X, then Y. In axis mode only the axis that is edited. An axis
// that the map has no such part of, and one the template marks read-only (the API would answer 400 read_only_axis, API 4.5), is not Reset's to change.
const AXIS_ORDER = ["dataAxis", "xAxis", "yAxis"];
const partOf = axisKey => (axisKey === "dataAxis" ? "data" : axisLetter(axisKey));

const resetAxisKeys = (map, axisEditingAxisName) =>
    (axisEditingAxisName ? [axisEditingAxisName] : AXIS_ORDER).filter(key => map.uploaded[key] && !map.uploaded[key].readOnly);

// One part of a reset: the explicit values of the upload are sent, never the screen's (a landed step replaces the table before the next one goes), the
// answer is applied for its part, and a failure is handleSaveFailure's, whose retry is this same step again with the same values. It answers null when the
// part is settled and the failure when it is not; when the user's Ok retry runs, its own outcome is the part's (a retry that lands lets the chain go on; a
// Cancel, a 400 or a retry that fails again stops it). A part that fails shows its upload values on screen first, because an earlier part's answer has put
// the working file's values back: the mark, the question and every later re-send are about what is on screen. `landed` collects what the answers said,
// for the step to report once.
const runResetPart = async (ctx, mapId, part, values, landed) => {
    const sent = await (part === "data" ? sendMapSave(ctx, mapId, values) : sendAxisSave(ctx, mapId, part, values));

    if (!sent.ok) {
        if (sent.error instanceof NoResponseError) landed.lost = true;
        const key = part === "data" ? "dataAxis" : AXIS_KEYS[part];
        ctx.setState(state => {
            const current = state.loadedMaps[mapId];
            if (!current) return null;

            // Only where a landed answer put the working file's values back (what is on screen is what `raw` holds). Any other values on screen are the
            // reset's own, or an edit typed since the step began, which a save queued behind the step is about to send.
            const onScreen = part === "data" ? current.modified.dataAxis.matrix.values : current.modified[key].matrix.values[0];
            const held = part === "data" ? currentValuesOf(current.raw) : currentAxisValuesOf(current.raw, part);
            if (!equals(onScreen, held)) return null;

            return {loadedMaps: {...state.loadedMaps, [mapId]: {...current, modified: tableWithUploadedAxes(current.modified, current.uploaded, [key])}}};
        });
        let retried;
        await handleSaveFailure(ctx, mapId, sent.error, async () => {
            retried = await runResetPart(ctx, mapId, part, values, landed);
        }, part);
        return retried === null ? null : sent.error;
    }
    if (sent.result === null) {
        // The values the reset put on screen are the ones the API holds: the part's mark comes off, as for a save that finds nothing to send.
        if (isPartUnsaved(ctx, mapId, part)) clearNotSaved(ctx, mapId, part);
        return null;
    }

    const {map, affectedMaps, affectedMapsState, changedBytes} = sent.result;
    const wasUnknown = isUnknown(ctx, mapId);
    applyOutcome(ctx, mapId, map, affectedMapsState, part);
    if (wasUnknown) resendOtherParts(ctx, mapId, part);
    landed.bytes += changedBytes;
    if (part !== "data") landed.axis = true;
    affectedMaps.filter(name => name !== mapId).forEach(name => landed.others.add(name));
    landed.any = true;
    return null;
};

// The step of a reset. The values of the upload go into the modified table first, so that the screen shows the reset at once (a new table, so that what
// compares tables sees it); then the parts are sent one after another, inline, with the upload's values, the data first. A part the send finds equal to
// what the API holds is not sent (the no-op suppression, which is off while the state of the map is unknown). The chain stops at the first failure that
// the user's retry does not settle, handleSaveFailure deals with it, and the map is read once (inline: refreshMap would wait behind this step). With no
// failure the last answer is the final state and nothing is read. The other open windows of the maps the answers affected are read again and the user is
// told, once for the whole reset. The hex viewer is told once of what the answers changed, and the other maps whose state is unknown are sent, as after
// any landed save.
const runReset = async (ctx, mapId, axisEditingAxisName) => {
    if (!isLoaded(ctx, mapId)) return;

    const keys = resetAxisKeys(ctx.getState().loadedMaps[mapId], axisEditingAxisName);
    const uploaded = ctx.getState().loadedMaps[mapId].raw.uploaded;

    // Only the parts that are sent: the others, including an unsaved edit of the data while an axis is reset, stay as the user left them.
    if (keys.length > 0) {
        ctx.setState(state => {
            const current = state.loadedMaps[mapId];
            return current ? {loadedMaps: {...state.loadedMaps, [mapId]: {...current, modified: tableWithUploadedAxes(current.modified, current.uploaded, keys)}}} : null;
        });
    }

    const landed = {bytes: 0, axis: false, any: false, lost: false, others: new Set()};
    let failure = null;
    for (const key of keys) {
        failure = await runResetPart(ctx, mapId, partOf(key), uploaded[key].values, landed);
        if (failure) break;
    }

    // A part whose answer was lost may have been written, so when the reset's own read lands after one the hex viewer is told (as after handleSaveFailure's own read).
    // When an earlier part landed the end of the step tells it as well, which is harmless: the viewer's refresher takes both as one.
    if (failure && (await reloadMap(ctx, mapId)) && landed.lost) ctx.hexDirty(true);
    if (landed.others.size > 0) {
        // Detached, as an axis save's: a step never waits on another map's queue.
        refreshOpenWindows(ctx, [...landed.others]);
        ctx.alert(COPY.axisAffected(landed.others.size), "danger");
    }
    if (landed.any) {
        ctx.hexDirty(landed.axis ? true : landed.bytes);
        resendUnknownMaps(ctx, mapId);
    }
};

/**
 * Puts a map back to what was uploaded (R53; EDI A5 "Reset"; never to the release original), as a step of the map's queue of the kind 'reset': it waits
 * for the write that is running and for those queued before it, and is never collapsed. In axis mode only the axis that is edited is reset; otherwise the
 * data, then X, then Y, one request after another (never together: the last answer to arrive would be the final state), each skipped when the map has no
 * such axis, the template marks it read-only, or it is already what the API holds. No question is asked. The failures are handleSaveFailure's.
 * @param {Object} ctx
 * @param {string} mapId
 * @param {?string} [axisEditingAxisName] - 'xAxis' or 'yAxis' for a window that is editing an axis, else null
 * @returns {Promise<void>} settles when the step does, whatever came of it; the re-reads of other windows are not waited for
 */
export const resetMap = (ctx, mapId, axisEditingAxisName = null) => enqueueMapWrite(ctx, mapId, {kind: "reset", part: null, run: () => runReset(ctx, mapId, axisEditingAxisName)});

// ---- Favourite ---------------------------------------------------------------------------------------------------------------------------

// What a failure of a toggle means for the user. It never rejects. The star stays as it was in every case: it follows the API's answer only.
//  - A 404 map_not_found: the map is not in this release, so its window closes without a word, what was queued for it is dropped, and it leaves
//    loadedMaps (the same as a save's).
//  - A 409 project_locked that the one retry did not outlast: the user is told that the project is busy.
//  - No response: a toggle is not idempotent, so it is never sent again (a second request could undo a first that was written), and the map is not
//    read: the next press answers with the true state, which the star follows. The toast is COPY.network, not COPY.noResponse: that row promises that
//    the editor will put the map right, which for a star it does not.
//  - Anything else is reported, with the map, and the user gets the copy table's row for its code or the generic text.
const handleFavouriteFailure = (ctx, mapId, error) => {
    if (error instanceof ApiError) {
        if (isMapNotFound(error)) {
            dropGoneMap(ctx, mapId);
            return;
        }

        if (error.status === 409 && error.is("project_locked")) {
            ctx.alert(COPY.project_locked, "danger");
            return;
        }
    } else if (error instanceof NoResponseError) {
        ctx.alert(COPY.network, "danger");
        return;
    }

    ctx.captureException(error, mapId);
    ctx.alert(copyFor(error), "danger");
};

// The maps whose toggle is out now, for each provider (its `mapWrites` is the one thing every ctx of a provider has in common, and the tests' harness has
// one of its own): a Set of map ids.
const togglesOut = new WeakMap();

/**
 * Stars or unstars a map (API 1.6; R53): the API toggles, and the star shows what it answers. This is not a step of the map's queue (the Fable ruling):
 * it changes no values of a map and reads nothing, so it writes with one functional setState, the answer's `favourited` into the loaded map (the map
 * and its last good payload, so that a rebuild from it keeps the star), if it is loaded, and into the sidebar's entries. A map that is not open is not
 * loaded by it. The failures are handleFavouriteFailure's.
 *
 * Nothing orders two toggles of one map, and their answers could land in the opposite order to their commits, so a press on a map whose toggle is still
 * out is ignored: no request, no toast. A landed toggle proves that the API can be reached, as a landed save does (R7), so the maps whose state is unknown
 * are sent again, the toggled map included (a toggle says nothing of its values, unlike a save). Detached: this does not wait for those queues.
 * @param {Object} ctx
 * @param {string} mapId
 * @returns {Promise<void>} settles when the toggle has, whatever came of it; at once for a press that is ignored
 */
export const toggleFavourite = async (ctx, mapId) => {
    if (!togglesOut.has(ctx.mapWrites)) togglesOut.set(ctx.mapWrites, new Set());
    const out = togglesOut.get(ctx.mapWrites);
    if (out.has(mapId)) return;

    out.add(mapId);
    let favourited;
    try {
        favourited = await MapController.toggleFavourite(ctx.projectId, mapId, ctx.retryOptions);
    } catch (error) {
        handleFavouriteFailure(ctx, mapId, error);
        return;
    } finally {
        out.delete(mapId);
    }

    ctx.setState(state => {
        const folderStructure = setMapFavourited(mapId, favourited, state.folderStructure);
        const current = state.loadedMaps[mapId];
        if (!current) return {folderStructure};

        return {folderStructure, loadedMaps: {...state.loadedMaps, [mapId]: {...current, favourited, raw: {...current.raw, favourited}}}};
    });
    resendUnknownMaps(ctx, null);
};
